import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { persistLead, captureBlockedLead } from "@/lib/lead-fallback.server";
import { confirmationEmail, internalLeadEmail, LEAD_INBOX, LEAD_NOTIFY, sendEmail } from "@/lib/email/resend.server";
import { checkRateLimit, getClientIp } from "@/lib/rate-limit.server";

const Schema = z.object({
  email: z.string().trim().toLowerCase().email().max(255),
  source_path: z.string().max(255).optional().nullable(),
  utm_source: z.string().max(120).optional().nullable(),
  utm_medium: z.string().max(120).optional().nullable(),
  utm_campaign: z.string().max(120).optional().nullable(),
  funnel_id: z.string().max(120).optional().nullable(),
  user_agent: z.string().max(500).optional().nullable(),
  session_id: z.string().uuid().nullable().optional(),
  visitor_id: z.string().uuid().nullable().optional(),
});

export const subscribeNewsletter = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => Schema.parse(input))
  .handler(async ({ data }) => {
    const ip = getClientIp(getRequest());
    if (!checkRateLimit({ scope: "newsletter", keys: [ip, data.email], limit: 5, windowMs: 60 * 60 * 1000 })) {
      await captureBlockedLead({
        flow: "newsletter",
        reason: "rate-limit",
        email: data.email,
        source_path: data.source_path ?? null,
        user_agent: data.user_agent ?? null,
        session_id: data.session_id ?? null,
        visitor_id: data.visitor_id ?? null,
      });
      return { ok: false as const, alreadySubscribed: false };
    }
    try {
      const { error } = await supabaseAdmin
        .from("newsletter_subscribers")
        .insert({
          email: data.email,
          source_path: data.source_path ?? null,
          utm_source: data.utm_source ?? null,
          utm_medium: data.utm_medium ?? null,
          utm_campaign: data.utm_campaign ?? null,
          funnel_id: data.funnel_id ?? null,
          user_agent: data.user_agent ?? null,
          session_id: data.session_id ?? null,
          visitor_id: data.visitor_id ?? null,
        });
      // Ignore unique-violation (already subscribed) — treat as success
      if (error && error.code !== "23505") {
        console.error("[newsletter] insert failed:", error);
        await persistLead("newsletter_subscribers", {
          email: data.email,
          source_path: data.source_path ?? null,
          utm_source: data.utm_source ?? null,
          utm_medium: data.utm_medium ?? null,
          utm_campaign: data.utm_campaign ?? null,
          funnel_id: data.funnel_id ?? null,
          user_agent: data.user_agent ?? null,
          session_id: data.session_id ?? null,
          visitor_id: data.visitor_id ?? null,
        }, {
          source: "newsletter",
          email: data.email,
          source_path: data.source_path ?? null,
          session_id: data.session_id ?? null,
          visitor_id: data.visitor_id ?? null,
        });
        return { ok: false as const, alreadySubscribed: false };
      }
      const alreadySubscribed = error?.code === "23505";
      if (!alreadySubscribed) {
        const internal = internalLeadEmail({
          subject: `Ny nyhetsbrev-prenumerant — ${data.email}`,
          heading: "Ny prenumerant på Fenny-brevet",
          fields: {
            "E-post": data.email,
            "Källa": data.source_path,
            "Kampanj": data.utm_campaign,
          },
        });
        const confirm = confirmationEmail({
          intro: "Tack för att du prenumererar på Fenny-brevet. Nästa utskick landar i din inkorg snart.",
        });
        await Promise.allSettled([
          sendEmail({ to: LEAD_NOTIFY, subject: internal.subject, html: internal.html, replyTo: data.email,
            logContext: { kind: "internal_lead", leadSource: "newsletter", leadEmail: data.email } }),
          sendEmail({ to: data.email, subject: confirm.subject, html: confirm.html, replyTo: LEAD_INBOX,
            logContext: { kind: "newsletter_confirmation", leadSource: "newsletter", leadEmail: data.email } }),
        ]);
      }
      return { ok: true as const, alreadySubscribed };
    } catch (e) {
      console.error("[newsletter] unexpected:", e);
      return { ok: false as const, alreadySubscribed: false };
    }
  });
